/**
 * Экран «Операции» — по макетам Figma «SKOKA · Операции», «Период» и
 * «Выбор счёта».
 *
 * Задача экрана — найти операцию и понять, куда ушли деньги за период.
 * Поэтому сверху три вопроса, которые человек задаёт первыми: по какому
 * счёту, за какое время и сколько всего пришло и ушло. Ниже — дни, каждый
 * своей карточкой с итогом: в длинной ленте глаз цепляется за дату, а не за
 * сотую строку.
 *
 * Фильтры живут, пока открыто приложение: ушёл на главную и вернулся —
 * выбранный счёт и период на месте. Период — общий с «Аналитикой»
 * (`period-filter.tsx`): по умолчанию от зарплаты до зарплаты. Итоги
 * считаются тем же подсчётом, что и «Итоги» аналитики (`periodFacts`):
 * возврат уменьшает расходы, а не добавляется к доходам; переводы между
 * своими счетами и внесение на кредитку — не расход.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ZERO, add, formatMoney, periodFacts, spendKind, sub, type Money, type SpendKind } from '@skoka/core';
import { AccountGlyph } from './account-glyph.js';
import { accountGroups } from './account-groups.js';
import { CategoryGlyph, colorOf } from './category-icon.js';
import { SheetWrap } from './sheet.js';
import { dismissHint, type AppState, type GoalEvent, type Tx } from './store.js';
import { dayMonth, plural } from './text.js';
import { Icon } from './icon.js';
import { SwipeRow, SwipeRowDemo } from './swipe-row.js';
import { SWIPE_ROWS_HINT, SwipeCoach, coachSeen, coachWanted } from './swipe-coach.js';
import { Tip } from './tip.js';
import {
  PeriodHint, PeriodSheet, PeriodTabs, Radio, currentChoice, rangeLabel, rangeOf, rangeText, sharedFilter,
  yearsOf, type RangeChoice, type RangeCtx,
} from './period-filter.js';
import { paydayOf, periodsOf } from './period-view.js';

const shift = (p: string, days: number): string => {
  const d = new Date(`${p}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + days); return d.toISOString().slice(0, 10);
};
/** Ключ подсказки о периоде от зарплаты до зарплаты. */
export const PERIOD_HINT = 'period-salary';

// ─────────────────────────────────────────────────────────── фильтры

type TypeFilter = 'all' | 'expense' | 'income' | 'transfer';
const TYPE_TITLE: Record<TypeFilter, string> = {
  all: 'Все', expense: 'Расходы', income: 'Поступления', transfer: 'Переводы',
};

/** Часть плана внутри «Расходов» — тем же правилом, что «Итоги» и «Обзор». */
export const PART_FILTER: readonly (readonly [SpendKind | null, string, string])[] = [
  [null, 'Все расходы', 'Все расходы'],
  ['need', 'Нужды', 'Повседневные нужды'],
  ['want', 'Свободные', 'Свободные траты'],
  ['obligations', 'Платежи', 'Обязательные платежи'],
];

/** Тип операций живёт, пока открыто приложение. Период и счёт — общие с «Аналитикой». */
const remembered: { type: TypeFilter; part: SpendKind | null } = { type: 'all', part: null };

/** Сумма со знаком, как в макете: «−17 012 ₽», «+85 000 ₽». */
const money = (m: Money): string => (m < ZERO ? `−${formatMoney(sub(ZERO, m))}`
  : m > ZERO ? `+${formatMoney(m)}` : formatMoney(m));

const PART_TITLE: Record<string, string> = { need: 'Повседневные нужды', want: 'Свободные траты' };


// ─────────────────────────────────────────────────────────── экран

export function Operations({ state, ask, onEdit, onDelete, onAdd, onChanged, onAdjust }: {
  state: AppState;
  /**
   * Карандаш у «На счёте сейчас»: «Скорректировать остаток» выбранного
   * счёта, у всех счетов — основного, с выбором в самом листе (27.10.2026).
   */
  onAdjust?: (accountId: string) => void;
  /** «Спроси SKOKA» в шапке экрана. */
  ask?: ReactNode;
  /** Записать операцию — то же окно, что «+». */
  onAdd: () => void;
  /** Открыть правку операции. */
  onEdit: (id: string) => void;
  /** Удалить операцию свайпом — с «Вернуть». */
  onDelete: (id: string) => void;
  /** Что-то записано (подсказка закрыта) — перечитать состояние. */
  onChanged?: () => void;
}) {
  const today = state.today;
  const ctx: RangeCtx = useMemo(() => {
    const payday = paydayOf(state);
    return { today, periods: periodsOf(state), ...(payday ? { payday } : {}) };
  }, [state, today]);
  const years = useMemo(() => yearsOf(state.transactions.map((t) => t.occurredDate), today), [state, today]);
  const [period, setPeriodRaw] = useState<RangeChoice>(() => currentChoice(ctx));
  const [accountId, setAccountRaw] = useState<string | null>(
    sharedFilter.accountId && state.accounts.some((a) => a.id === sharedFilter.accountId) ? sharedFilter.accountId : null,
  );
  const [type, setTypeRaw] = useState<TypeFilter>(remembered.type);
  const [part, setPartRaw] = useState<SpendKind | null>(remembered.part);
  const [query, setQuery] = useState(() => sharedFilter.query ?? '');
  const [searching, setSearching] = useState(() => !!sharedFilter.query);
  useEffect(() => { sharedFilter.query = null; }, []);
  const [sheet, setSheet] = useState<'period' | 'account' | null>(null);

  const setPeriod = (p: RangeChoice) => { sharedFilter.range = p; setPeriodRaw(p); };
  const setAccount = (id: string | null) => { sharedFilter.accountId = id; setAccountRaw(id); };
  const setType = (t: TypeFilter) => { remembered.type = t; setTypeRaw(t); };
  const setPart = (k: SpendKind | null) => { remembered.part = k; setPartRaw(k); };
  /** Часть плана действует только внутри «Расходов». */
  const partOn = type === 'expense' ? part : null;
  const partTitle = PART_FILTER.find(([k]) => k === partOn)?.[2];

  const account = accountId ? state.accounts.find((a) => a.id === accountId) : undefined;
  const cat = (id?: string) => state.categories.find((c) => c.id === id);
  const accName = (id?: string) => state.accounts.find((a) => a.id === id)?.name ?? '—';

  const { from, to } = rangeOf(period, ctx);
  /** Операции периода и счёта — от них считаются итоги. */
  const inPeriod = useMemo(() => state.transactions.filter((t) => {
    if (from && t.occurredDate < from) return false;
    if (to && t.occurredDate > to) return false;
    if (accountId && t.accountId !== accountId && t.counterAccountId !== accountId) return false;
    return true;
  }), [state.transactions, from, to, accountId]);

  // Итоги — тем же подсчётом, что «Итоги» аналитики: возврат уменьшает
  // расходы, а не добавляется к доходам.
  const facts = periodFacts({
    txs: inPeriod,
    from: from ?? ('0000-01-01' as typeof today),
    to: to ?? ('9999-12-31' as typeof today),
    creditCardIds: state.accounts.filter((a) => a.kind === 'credit_card').map((a) => a.id),
  });
  const income = facts.income;
  const expense = add(facts.need, facts.want, facts.obligations);
  // Свои деньги — без кредитных карт: долг по карте не деньги на счетах.
  const own = state.accounts.filter((a) => !a.archived && a.kind !== 'credit_card');
  const onAccounts = account ? account.balance : add(...own.map((a) => a.balance));

  /** Что видно в списке: тип и поиск сужают его, итоги не трогают. */
  const q = query.trim().toLowerCase();
  const titleOf = (t: Tx): string => (t.type === 'transfer'
    // Взнос в цель — переводом, но читается как взнос, а не просто «Перевод».
    ? (t.link?.kind === 'goal' ? `В цель «${state.goals.find((g) => g.id === (t.link as { goalId: string }).goalId)?.name ?? 'цель'}»` : 'Перевод')
    : t.type === 'adjustment' ? 'Корректировка'
    : cat(t.categoryId)?.name ?? (t.type === 'income' ? (t.note || 'Поступление') : 'Без категории'));
  const shown = inPeriod.filter((t) => {
    if (type !== 'all' && t.type !== type) return false;
    if (partOn && spendKind(t) !== partOn) return false;
    if (!q) return true;
    const hay = [titleOf(t), t.note ?? '', accName(t.accountId), t.counterAccountId ? accName(t.counterAccountId) : '',
      formatMoney(t.amount), String(t.amount / 100)].join(' ').toLowerCase();
    return hay.includes(q);
  });

  const days = useMemo(() => byDay(shown), [shown]);

  /*
    Свайп по строке не видно, пока его не показали. Учим при первом входе —
    на примере строки, даже если своих операций ещё нет: «Понятно» — и
    открывается сам экран, пустой или со списком.
  */
  const coach = sheet === null && coachWanted(state.dismissedHints, SWIPE_ROWS_HINT) && (
    <SwipeCoach kind="rows" title="Изменить и удалить — свайпом" demo={<SwipeRowDemo />}
      text={'Проведи по операции влево: карандаш — изменить, корзина — удалить. Длинный свайп удаляет сразу, вернуть можно 5\u00a0секунд.'}
      onDone={() => void coachSeen(SWIPE_ROWS_HINT)} />
  );

  if (state.transactions.length === 0) {
    return (
      <>
        <header className="pgtitle"><h1>Операции</h1>{ask}</header>
        {/* Своя кнопка, а не «нажми «+»»: «+» в меню новички не замечают. */}
        <div className="empty ops-empty">
          <b>Операций пока нет</b>
          <p>Здесь будут все траты и поступления. Записать можно вручную, голосом или снимком чека.</p>
          <button className="btn pri" onClick={onAdd}><Icon name="plus" size={16} />Записать операцию</button>
        </div>
        {coach}
      </>
    );
  }

  // У всех счетов карандаш открывает основной счёт для трат; сменить — в самом листе.
  const adjustTarget = account?.id
    ?? state.accounts.find((a) => !a.archived && a.isLiquid && a.kind !== 'credit_card')?.id
    ?? state.accounts.find((a) => !a.archived)?.id ?? null;

  return (
    <div className="ops">
      <header className="ops-head">
        <button className="ops-account" onClick={() => setSheet('account')}
          aria-label={`Счёт: ${account?.name ?? 'Все счета'}. Сменить`}>
          {/* Значок счёта — как в листе выбора: сразу видно, какой выбран. */}
          {account ? <AccountGlyph account={account} size="s" />
            : <span className="catico c-gray s-s ops-all-ico" aria-hidden="true"><Icon name="layer-group" size={15} /></span>}
          <span>{account?.name ?? 'Все счета'}</span>
          <Icon name="chevron-right" size={16} />
        </button>
        <div className="ops-tools">
          <button className={`ops-search${searching ? ' on' : ''}`} aria-label="Поиск операций" aria-pressed={searching}
            onClick={() => { setSearching(!searching); if (searching) setQuery(''); }}>
            <Icon name="magnifying-glass" size={18} />
          </button>
          {ask}
        </div>
      </header>

      <PeriodTabs choice={period} ctx={ctx} years={years} onPick={setPeriod} onOpen={() => setSheet('period')} />
      {period.kind === 'salary' && !state.dismissedHints.includes(PERIOD_HINT) && (
        <PeriodHint ctx={ctx} onDismiss={async () => { await dismissHint(PERIOD_HINT); onChanged?.(); }} />
      )}

      {searching && (
        <label className="ops-find">
          <Icon name="magnifying-glass" size={18} />
          <input type="search" value={query} autoFocus placeholder="Поиск операций" aria-label="Поиск операций"
            onChange={(e) => setQuery(e.target.value)} />
          {query && (
            <button aria-label="Очистить поиск" onClick={() => setQuery('')}><Icon name="xmark" size={16} /></button>
          )}
        </label>
      )}

      <section className="ops-sum" aria-label={`Итоги: ${rangeText(period, ctx)}`}>
        <div className="ops-sum-row">
          <div className="ops-cell">
            <span className="k">Доходы
              <Tip>Поступления за период. Возвраты сюда не входят — они уменьшают расходы. Переводы между своими счетами тоже не входят.</Tip></span>
            <b className="num pos">{formatMoney(income)}</b>
          </div>
          <i className="ops-vline" />
          <div className="ops-cell">
            <span className="k">Расходы
              <Tip>Траты за период, включая оплату платежей, за вычетом возвратов. Переводы между своими счетами и внесение на кредитку сюда не входят.</Tip></span>
            <b className="num">{money(sub(ZERO, expense))}</b>
          </div>
        </div>
        <i className="ops-hline" />
        <div className="ops-sum-row">
          <div className="ops-cell">
            <span className="k">Операций</span>
            <b className="num sm pos">{inPeriod.length}</b>
          </div>
          <i className="ops-vline" />
          <div className="ops-cell">
            <span className="k">{account ? 'На счёте сейчас' : 'На счетах сейчас'}</span>
            <b className="num sm withpen">
              {onAdjust && adjustTarget && (
                <button className="penbtn" aria-label="Скорректировать остаток" onClick={() => onAdjust(adjustTarget)}>
                  <Icon name="pen" size={14} />
                </button>
              )}
              {formatMoney(onAccounts)}
            </b>
          </div>
        </div>
      </section>

      {/* Тип — на виду, прямо над списком, который он сужает. Прежде он прятался
          в «⋯», и его никто не находил. Итоги выше от него не меняются. */}
      <div className="ptabs kinds" role="tablist" aria-label="Какие операции показывать">
        {(Object.keys(TYPE_TITLE) as TypeFilter[]).map((k) => (
          <button key={k} role="tab" aria-selected={type === k} className={type === k ? 'on' : ''}
            onClick={() => setType(k)}>{TYPE_TITLE[k]}</button>
        ))}
      </div>
      {/* Внутри «Расходов» — часть плана: те же три строки, что в «Обзоре». */}
      {type === 'expense' && (
        <div className="ptabs sub" role="tablist" aria-label="Часть плана">
          {PART_FILTER.map(([k, short, full]) => (
            <button key={short} role="tab" aria-selected={part === k} aria-label={full} className={part === k ? 'on' : ''}
              onClick={() => setPart(k)}>{full}</button>
          ))}
        </div>
      )}

      {days.length === 0 ? (
        <div className="ops-none">
          <b>{q ? 'Ничего не нашлось' : `${rangeLabel(period, ctx)}: операций нет`}</b>
          <span>
            {partTitle && partOn ? `Показаны только ${partTitle.toLowerCase()}.`
              : type !== 'all' ? `Показаны только ${TYPE_TITLE[type].toLowerCase()}.` : rangeText(period, ctx)}
          </span>
          {period.kind !== 'all' && (
            <button className="btn" onClick={() => setPeriod({ kind: 'all' })}>
              {q ? 'Искать за всё время' : 'Показать всё время'}
            </button>
          )}
        </div>
      ) : <OpDays state={state} days={days} accountId={accountId} onOpen={onEdit} onDelete={onDelete} />}
      {coach}

      {sheet === 'period' && (
        <PeriodSheet value={period} ctx={ctx} years={years}
          context={`${account?.name ?? 'Все счета'} · ${rangeText(period, ctx)}`}
          onClose={() => setSheet(null)}
          onApply={(p) => { setPeriod(p); setSheet(null); }} />
      )}
      {sheet === 'account' && (
        <AccountSheet state={state} value={accountId}
          context={`Операции: ${rangeLabel(period, ctx).toLowerCase()}`}
          onClose={() => setSheet(null)}
          onApply={(id) => { setAccount(id); setSheet(null); }} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────── дни и строки

/** Операции по дням, новые сверху. */
/**
 * Время операции «14:20» — в строке рядом со счётом (15.10.2026).
 *
 * Время хранится местным, в `occurredAt`. Запись задним числом без времени
 * получает ровно полдень — это заглушка, а не время покупки: её не
 * показываем, чтобы не выдумывать «12:00» там, где человек его не называл.
 */
export function opTime(t: Pick<Tx, 'occurredAt'>): string {
  const hm = t.occurredAt.slice(11, 16);
  return /^\d{2}:\d{2}$/.test(hm) && !t.occurredAt.endsWith('T12:00:00.000Z') ? hm : '';
}

export function byDay(txs: readonly Tx[]): [string, Tx[]][] {
  const map = new Map<string, Tx[]>();
  for (const t of [...txs].sort((a, b) => (a.occurredAt < b.occurredAt ? 1 : -1))) {
    const list = map.get(t.occurredDate) ?? [];
    list.push(t);
    map.set(t.occurredDate, list);
  }
  return [...map.entries()];
}

/**
 * Дни с операциями — одна разметка для «Операций» и листа категории в
 * «Аналитике»: одна и та же операция не должна выглядеть по-разному.
 *
 * `accountId` — выбранный счёт: перевод с него — минус, на него — плюс.
 *
 * Свайп строки влево открывает «Изменить» и «Удалить»; тап — правку.
 */
export function OpDays({ state, days, allocations = [], accountId, onOpen, onDelete }: {
  state: AppState; days: readonly [string, Tx[]][]; accountId: string | null;
  /** Взносы без финансовой операции: только для истории цели. */
  allocations?: readonly GoalEvent[];
  onOpen: (id: string) => void; onDelete: (id: string) => void;
}) {
  const today = state.today;
  /** Строка, у которой открыты кнопки. */
  const [swiped, setSwiped] = useState<string | null>(null);
  /*
    Тап мимо открытой строки закрывает её — и только: если тот же тап
    пришёлся на другую строку, правку он не открывает.
  */
  const closing = useRef(false);
  useEffect(() => {
    if (!swiped) return;
    const away = (e: PointerEvent) => {
      if ((e.target as Element | null)?.closest('.ops-swipe.open')) return;
      closing.current = true;
      setSwiped(null);
      window.setTimeout(() => { closing.current = false; }, 400);
    };
    document.addEventListener('pointerdown', away, true);
    return () => document.removeEventListener('pointerdown', away, true);
  }, [swiped]);
  const cat = (id?: string) => state.categories.find((c) => c.id === id);
  /** Как операция меняет деньги выбранного счёта (или всех своих денег). */
  const signed = (t: Tx): Money => {
    if (t.type === 'expense') return sub(ZERO, t.amount);
    if (t.type === 'income') return t.amount;
    // Корректировка — уже со знаком: минус — денег оказалось меньше.
    if (t.type === 'adjustment') return !accountId || t.accountId === accountId ? t.amount : ZERO;
    if (t.type === 'transfer' && accountId) {
      if (t.accountId === accountId) return sub(ZERO, t.amount);
      if (t.counterAccountId === accountId) return t.amount;
    }
    return ZERO;
  };
  const titleOf = (t: Tx): string => (t.type === 'transfer'
    // Взнос в цель — переводом, но читается как взнос, а не просто «Перевод».
    ? (t.link?.kind === 'goal' ? `В цель «${state.goals.find((g) => g.id === (t.link as { goalId: string }).goalId)?.name ?? 'цель'}»` : 'Перевод')
    : t.type === 'adjustment' ? 'Корректировка'
    : cat(t.categoryId)?.name ?? (t.type === 'income' ? (t.note || 'Поступление') : 'Без категории'));
  /** Счёт: его значок в его цвете и название, которое сокращается «…». */
  const accTag = (id: string | undefined, side?: 'from' | 'to') => {
    const a = state.accounts.find((x) => x.id === id);
    return (
      <span className={`ops-acctag${side ? ` ${side}` : ''}`}>
        {a && <AccountGlyph account={a} size="f" />}
        <span className="ops-accname">{a?.name ?? '—'}</span>
      </span>
    );
  };
  /*
    Строка операции (15.10.2026, docs/design/oprow-2026-10-15), по образцу
    Т-Банка: справа сумма и под ней счёт, слева название — на что ушли
    деньги — и под ним «время · что это · комментарий». Название платежа,
    долга, кредитки и цели — их собственное: платёж по ипотеке прежде
    назывался «Прочее», погашение кредитки — «Перевод».
  */
  const rowOf = (t: Tx, c: ReturnType<typeof cat>, refund: boolean): { title: string; line2: string; icon: ReactNode } => {
    const link = t.link;
    const ob = link?.kind === 'obligation' ? state.obligations.find((o) => o.id === link.obligationId) : undefined;
    const prepay = link?.kind === 'loan';
    const debt = link?.kind === 'loan' ? state.loans.find((l) => l.id === link.loanId)
      : ob ? state.loans.find((l) => l.obligationId === ob.id) : undefined;
    const isDebt = prepay || !!debt || ob?.kind === 'loan';
    const goal = link?.kind === 'goal' ? state.goals.find((g) => g.id === link.goalId) : undefined;
    const card = t.type === 'transfer' ? state.accounts.find((a) => a.id === t.counterAccountId && a.kind === 'credit_card') : undefined;
    // Часть плана — только там, где её выбирают при записи («Прочее»): у
    // остальных категорий она своя и повторяла бы название.
    const mixed = t.type === 'expense' && !ob && !prepay && (!c || c.defaultPart == null || (!!t.part && t.part !== c.defaultPart));
    const title = ob ? ob.name
      : debt ? debt.name
      : prepay ? (t.note ?? '').replace(/^Досрочно: /, '')
      : goal ? goal.name
      : card ? card.name
      : titleOf(t);
    // Что это за операция — подпись ставит приложение, по виду долга: у
    // личного долга нет «кредита» и «погашения».
    const what = isDebt ? (debt?.kind === 'private' ? 'Возврат долга'
        : prepay ? 'Досрочный платёж'
        : debt?.kind === 'installment' ? 'Платёж по рассрочке' : 'Платёж по кредиту')
      : ob ? (c && c.id !== 'other' ? c.name : 'Обязательный платёж')
      : goal ? 'Взнос'
      : card ? 'Погашение'
      : refund ? 'Возврат'
      : mixed ? PART_TITLE[t.part ?? ''] ?? ''
      : t.type === 'adjustment' ? (t.initial !== undefined && t.initial !== t.amount
        ? `уточнилась, была ${money(t.initial)}` : t.target !== undefined ? `стало ${formatMoney(t.target)}` : '')
      : '';
    // Комментарий человека — у любой операции. Заметки, которые приложение
    // ставит само (имя платежа, «Досрочно: …», «Взнос в цель «…»»), не повторяем.
    const auto = !t.note || t.note === ob?.name || /^Досрочно: /.test(t.note) || /^Взнос в цель/.test(t.note) || t.note === title;
    const line2 = [opTime(t), what, auto ? '' : t.note].filter(Boolean).join(' · ');
    const icon = goal
      ? <CategoryGlyph icon={goal.kind === 'emergency_fund' ? 'umbrella' : 'bullseye-arrow'} color={goal.kind === 'emergency_fund' ? 'blue' : 'green'} />
      : card ? <AccountGlyph account={card} />
      : isDebt
        // Значки долга — как в выборе долга при записи.
        ? <CategoryGlyph icon={debt?.kind === 'installment' ? 'cart-shopping' : debt?.kind === 'private' ? 'user' : 'building-columns'}
          color={debt?.kind === 'installment' ? 'amber' : debt?.kind === 'private' ? 'gray' : 'blue'} />
      : t.type === 'transfer' || t.type === 'adjustment'
        ? <span className="catico c-gray" aria-hidden="true"><Icon name={t.type === 'adjustment' ? 'scale-balanced' : 'arrow-right-arrow-left'} size={20} /></span>
        : <CategoryGlyph icon={c?.icon || (t.type === 'income' ? 'arrow-trend-up' : 'circle-ellipsis')}
          color={c ? colorOf(c) : t.type === 'income' ? 'blue' : 'gray'} />;
    return { title, line2, icon };
  };
  const eventDays = new Map<string, GoalEvent[]>();
  for (const e of allocations) {
    const list = eventDays.get(e.date) ?? [];
    list.push(e); eventDays.set(e.date, list);
  }
  const grouped = new Map(days);
  for (const date of eventDays.keys()) if (!grouped.has(date)) grouped.set(date, []);
  const shownDays = allocations.length === 0 ? days : [...grouped].sort(([a], [b]) => b.localeCompare(a));
  const dayTitle = (date: string): string => (date === today ? 'Сегодня'
    : date === shift(today, -1) ? 'Вчера' : dayMonth(date, today));
  return (
    <>
      {shownDays.map(([date, list]) => {
        const net = add(...list.map(signed));
        return (
          <section className="ops-day" key={date}>
            <div className="ops-dayhead">
              <b>{dayTitle(date)}</b>
              <span className="num">{net === ZERO ? '' : money(net)}</span>
            </div>
            <div className="ops-list">
              {list.map((t) => {
                const c = cat(t.categoryId);
                const amount = signed(t);
                const refund = t.type === 'income' && t.link?.kind === 'income' && t.link.incomeKind === 'refund';
                const r = rowOf(t, c, refund);
                return (
                  <SwipeRow key={t.id} open={swiped === t.id} label={r.title} {...(t.type === 'transfer' ? { className: 'tr' } : {})}
                    onSwipe={(on) => setSwiped(on ? t.id : null)}
                    onTap={() => { if (!closing.current) onOpen(t.id); }}
                    onEdit={() => { setSwiped(null); onOpen(t.id); }}
                    onDelete={() => { setSwiped(null); onDelete(t.id); }}>
                    {r.icon}
                    <span className="ops-text">
                      <b>{r.title}</b>
                      {r.line2 && <span>{r.line2}</span>}
                    </span>
                    <span className="ops-right">
                      <span className={`ops-amt num${amount > ZERO ? ' pos' : amount === ZERO ? ' mute' : ''}`}>
                        {amount === ZERO ? formatMoney(t.amount) : money(amount)}
                      </span>
                      <span className="ops-where">
                        {t.type === 'transfer'
                          ? <>{accTag(t.accountId, 'from')}<span className="ops-arrow"><Icon name="arrow-right" size={9} /></span>{accTag(t.counterAccountId, 'to')}</>
                          : accTag(t.accountId)}
                      </span>
                    </span>
                  </SwipeRow>
                );
              })}
              {(eventDays.get(date) ?? []).map((e) => (
                <div className="ops-row tr" key={`goal-event:${e.key}`}>
                  <CategoryGlyph icon="bullseye-arrow" color="green" />
                  <span className="ops-text">
                    <b>Взнос в цель «{state.goals.find((g) => g.id === e.goalId)?.name ?? 'цель'}»</b>
                    <span>{e.via === 'reserve' ? 'На том же счёте' : e.via === 'transfer' ? 'Перевод в цель' : 'Без перевода'}</span>
                  </span>
                  <span className="ops-right"><span className="ops-amt num mute">{formatMoney(e.amount)}</span></span>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </>
  );
}

// ─────────────────────────────────────────────────────────── листы

export function AccountSheet({ state, value, context, action = 'Показать операции', onClose, onApply, title = 'Выбор счёта', all = true, detail }: {
  state: AppState; value: string | null; context: string;
  /** Подпись кнопки внизу листа. */
  action?: string;
  onClose: () => void; onApply: (id: string | null) => void;
  title?: string;
  /** Строка «Все счета» — у фильтра есть, у выбора счёта для корректировки нет. */
  all?: boolean;
  /** Серая строка под счётом: «сверен 12 октября»; `warn` — жёлтая. */
  detail?: (a: AppState['accounts'][number]) => { text: string; warn?: boolean } | null;
}) {
  const [picked, setPicked] = useState<string | null>(value);
  const live = state.accounts.filter((a) => !a.archived);
  const own = live.filter((a) => a.kind !== 'credit_card');
  // Группы — как на «Счетах»: тип счёта стоит заголовком, под именем его
  // повторять незачем.
  const groups = accountGroups(live, state.accountGroups);
  const row = (a: typeof live[number]) => (
    <button key={a.id} className={`ops-acc${picked === a.id ? ' on' : ''}`} aria-pressed={picked === a.id}
      onClick={() => setPicked(a.id)}>
      <AccountGlyph account={a} />
      <span className="ops-text">
        <b>{a.name}</b>
        {(() => {
          const dt = detail?.(a);
          if (dt) return <span className={dt.warn ? 'warn' : undefined}>{dt.text}</span>;
          return a.kind === 'credit_card' && a.balance < ZERO ? <span>Задолженность</span> : null;
        })()}
      </span>
      <span className="ops-acc-sum num">{money(a.balance).replace(/^\+/, '')}</span>
      <Radio on={picked === a.id} />
    </button>
  );

  return (
    <SheetWrap title={title} onClose={onClose} className="ops-sheet">
      <p className="ops-context">{context}</p>
      {all && (
        <button className={`ops-all${picked === null ? ' on' : ''}`} aria-pressed={picked === null}
          onClick={() => setPicked(null)}>
          <Icon name="layer-group" size={22} />
          <span className="ops-text">
            <b>Все счета</b>
            <span>Свои деньги · {formatMoney(add(ZERO, ...own.map((a) => a.balance)))}</span>
          </span>
          <Radio on={picked === null} />
        </button>
      )}
      {groups.map((g) => (
        <div key={g.key}>
          <div className="ops-group grp"><span>{g.title}</span><span className="num">{money(g.total).replace(/^\+/, '')}</span></div>
          {g.accounts.map(row)}
        </div>
      ))}
      <div className="impbar">
        <button className="btn pri" onClick={() => onApply(picked)}>{action}</button>
      </div>
    </SheetWrap>
  );
}

/** Для проверок: сколько операций за период — словами. */
export const countText = (n: number): string => plural(n, 'операция', 'операции', 'операций');
